/**
 * Merchant category codes (MCC): the 4-digit code card networks give each store. Card rewards
 * are paid by these codes (for example 4x at restaurants), so store presets keep one, which the
 * card optimizer can use later.
 */
export interface MccInfo {
  code: string;
  label: string;
  /** The ledger category that usually fits. */
  category: string;
  /** How card rewards usually treat it. */
  rewards:
    | "dining"
    | "grocery"
    | "gas"
    | "travel"
    | "wholesale"
    | "drugstore"
    | "online"
    | "home"
    | "streaming"
    | "entertainment"
    | "other";
}

export const MCC_CODES: MccInfo[] = [
  { code: "5300", label: "Wholesale club", category: "Groceries", rewards: "wholesale" },
  { code: "5411", label: "Grocery store / supermarket", category: "Groceries", rewards: "grocery" },
  { code: "5499", label: "Specialty food store", category: "Groceries", rewards: "grocery" },
  { code: "5812", label: "Restaurant", category: "Restaurants", rewards: "dining" },
  { code: "5813", label: "Bar / nightclub", category: "Restaurants", rewards: "dining" },
  { code: "5814", label: "Fast food", category: "Restaurants", rewards: "dining" },
  { code: "5541", label: "Gas station", category: "Gas", rewards: "gas" },
  { code: "5542", label: "Fuel dispenser", category: "Gas", rewards: "gas" },
  { code: "5912", label: "Drugstore / pharmacy", category: "Health", rewards: "drugstore" },
  { code: "5311", label: "Department store", category: "Household", rewards: "other" },
  { code: "5310", label: "Discount store", category: "Household", rewards: "other" },
  { code: "5651", label: "Clothing store", category: "Clothing", rewards: "other" },
  { code: "5732", label: "Electronics store", category: "Electronics", rewards: "other" },
  { code: "5942", label: "Bookstore", category: "Other", rewards: "other" },
  { code: "4121", label: "Taxi / rideshare", category: "Transport", rewards: "travel" },
  { code: "4111", label: "Transit", category: "Transport", rewards: "travel" },
  { code: "3000", label: "Airline", category: "Travel", rewards: "travel" },
  { code: "7011", label: "Hotel", category: "Travel", rewards: "travel" },
  { code: "7832", label: "Movie theater", category: "Fun", rewards: "entertainment" },
  { code: "7922", label: "Theater / concert tickets", category: "Fun", rewards: "entertainment" },
  { code: "7996", label: "Amusement park", category: "Fun", rewards: "entertainment" },
  { code: "5964", label: "Online / catalog merchant", category: "Shopping", rewards: "online" },
  { code: "5969", label: "Online / direct marketing", category: "Shopping", rewards: "online" },
  { code: "5200", label: "Home improvement store", category: "Household", rewards: "home" },
  { code: "4899", label: "Streaming / cable", category: "Fun", rewards: "streaming" },
  { code: "4829", label: "Money transfer", category: "Other", rewards: "other" },
];

export function mccInfo(code: string | null | undefined): MccInfo | null {
  if (!code) return null;
  return MCC_CODES.find((m) => m.code === code) ?? null;
}

/** A best guess from the store's name, for stores the scan or the AI didn't classify. */
export function guessMcc(store: string): MccInfo | null {
  const s = store.toLowerCase();
  const by = (code: string) => mccInfo(code);
  if (/costco|sam'?s club|bj'?s/.test(s)) return by("5300");
  if (
    /trader joe|whole foods|safeway|ralphs|vons|albertsons|99 ranch|h ?mart|sprouts|aldi|kroger|grocery|market/.test(
      s,
    )
  )
    return by("5411");
  if (
    /taco bell|mcdonald|in-n-out|chipotle|subway|wendy|burger|panda express|food court|\bfc\b/.test(
      s,
    )
  )
    return by("5814");
  if (/pho|din tai fung|restaurant|kitchen|grill|cafe|bistro|sushi|ramen|bbq|thai|pizza/.test(s))
    return by("5812");
  if (/shell|chevron|arco|76|exxon|mobil|gas/.test(s)) return by("5541");
  if (/cvs|walgreens|rite aid|pharmacy/.test(s)) return by("5912");
  if (/target|walmart|marshalls|tj ?maxx|ross/.test(s)) return by("5310");
  if (/best buy|apple store|micro center/.test(s)) return by("5732");
  if (/uber|lyft|taxi/.test(s)) return by("4121");
  if (/amazon|ebay|etsy|temu|shein|wayfair|chewy|\.com\b|online/.test(s)) return by("5964");
  if (/home depot|lowe'?s|ace hardware|ikea/.test(s)) return by("5200");
  if (/netflix|spotify|hulu|disney\+|youtube premium|hbo|max\b/.test(s)) return by("4899");
  if (/amc|regal|cinemark|ticketmaster|disneyland|six flags/.test(s)) return by("7832");
  return null;
}
